import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ClipboardCheck, Clock, Target, Trophy, ChevronRight,
  CheckCircle2, XCircle, HelpCircle
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const LearnerAssessmentsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const variant = user?.role === "manager" ? "manager" : "learner";
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get("/assessments");
        setAssessments((res.data || []).filter(a => a.is_published));
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  return (
    <AdminLayout title="Assessments" variant={variant}>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }} data-testid="learner-assessments-title">
            My Assessments
          </h2>
          <p className="text-muted-foreground">{assessments.length} available assessments</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading assessments...</div>
        ) : assessments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ClipboardCheck className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-1">No assessments available</h3>
              <p className="text-muted-foreground">Check back later for new assessments</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {assessments.map((a, i) => (
              <motion.div key={a.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Card
                  className="card-hover cursor-pointer"
                  onClick={() => navigate(`/learner/assessments/${a.id}`)}
                  data-testid={`learner-assessment-${a.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                          <HelpCircle className="w-6 h-6 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-base">{a.title}</h3>
                          {a.description && <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{a.description}</p>}
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1"><HelpCircle className="w-3.5 h-3.5" />{a.questions?.length || 0} questions</span>
                            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{a.time_limit_minutes} min</span>
                            <span className="flex items-center gap-1"><Target className="w-3.5 h-3.5" />Pass: {a.passing_score}%</span>
                            <span className="flex items-center gap-1"><Trophy className="w-3.5 h-3.5" />Max {a.max_attempts} attempts</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 ml-4 shrink-0">
                        <Button size="sm" className="gap-1" data-testid={`take-assessment-${a.id}`}>
                          Take Quiz <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default LearnerAssessmentsPage;
